import React, { useState, useEffect } from "react";
import { dateFilters } from "../../Dashboard/utils/dateFilters";
import DateFilter from "../TableUtils/DateFilter";

export default function TransactionTable({ transactions = [] }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dateFilter, setDateFilter] = useState("This Month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "created_on",
    direction: "desc",
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Dynamic rows per page based on container height
  useEffect(() => {
    const rowHeight = 36;
    const headerHeight = 36;
    const containerHeight = 400; // fixed height of table container, adjust as needed
    const newItemsPerPage = Math.max(
      1,
      Math.floor((containerHeight - headerHeight) / rowHeight)
    );
    setItemsPerPage(newItemsPerPage);
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key)
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "asc" };
    });
  };

  const SortArrow = ({ columnKey }) =>
    sortConfig.key !== columnKey ? null : (
      <span className="ml-1 text-xs">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );

  const parsePHDate = (str, endOfDay = false) => {
    const [y, m, d] = str.split("-").map(Number);
    const date = new Date(y, m - 1, d);

    if (endOfDay) {
      date.setHours(23, 59, 59, 999); // FULL day
    }

    return date;
  };

  // Date filtering
  const applyDateFilter = (data) => {
    if (dateFilter === "All") return data;

    let start = null;
    let end = null;

    if (dateFilter === "Custom Range" && customStart && customEnd) {
      start = parsePHDate(customStart);
      end = parsePHDate(customEnd, true);
    } else if (dateFilters[dateFilter]) {
      [start, end] = dateFilters[dateFilter]();
    }
    return data.filter((t) => {
      const created = new Date(t.created_on);
      return !start || (created >= start && created <= end);
    });
  };

  const filteredByDate = applyDateFilter(transactions);

  // Sorting
  const sortedData = [...filteredByDate].sort((a, b) => {
    const { key, direction } = sortConfig;
    let valA = key === "item.name" ? a.item?.name ?? "" : a[key] ?? "";
    let valB = key === "item.name" ? b.item?.name ?? "" : b[key] ?? "";

    if (typeof valA === "string") valA = valA.toLowerCase();
    if (typeof valB === "string") valB = valB.toLowerCase();

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });

  // Search
  const filteredData = sortedData.filter((row) => {
    const term = search.toLowerCase();
    return (
      row.item?.name?.toLowerCase().includes(term) ||
      row.type?.toLowerCase().includes(term) ||
      row.reason?.toLowerCase().includes(term) ||
      (row.created_on &&
        new Date(row.created_on).toLocaleString().toLowerCase().includes(term))
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIdx = (page - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div className="p-4 border rounded-lg">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="px-3 py-1.5 border rounded-lg text-sm w-56 focus:outline-none focus:ring focus:ring-blue-200"
        />

        {/* ✅ Extracted Date Filter */}
        <DateFilter
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
          onFilterChange={() => setPage(1)}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100 text-left select-none">
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("item.name")}
              >
                Item <SortArrow columnKey="item.name" />
              </th>
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("type")}
              >
                Type <SortArrow columnKey="type" />
              </th>
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("quantity")}
              >
                Quantity <SortArrow columnKey="quantity" />
              </th>
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("reason")}
              >
                Reason <SortArrow columnKey="reason" />
              </th>
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("created_on")}
              >
                Created <SortArrow columnKey="created_on" />
              </th>
              <th className="px-4 py-2 border-b font-medium text-gray-600">
                Created by
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((t, idx) => (
                <tr key={idx} className={`hover:bg-gray-50 transition `}>
                  <td className="px-4 py-2 border-b text-gray-700">
                    {t.item?.name || "Unknown"}
                  </td>
                  <td
                    className={`px-4 py-2 border-b font-medium ${
                      t.type === "Stock in"
                        ? "text-green-600"
                        : t.type === "Stock out"
                        ? "text-red-600"
                        : "text-gray-700"
                    }`}
                  >
                    {t.type}
                  </td>
                  <td className="px-4 py-2 border-b text-gray-700">
                    {t.quantity} {t.item?.unit || ""}
                  </td>
                  <td className="px-4 py-2 border-b text-gray-700">
                    {t.reason || "-"}
                  </td>
                  <td className="px-4 py-2 border-b text-gray-700">
                    {new Date(t.created_on).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 border-b text-gray-700">
                    {t.created_by?.name || "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > itemsPerPage && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <span>
            🔢 Total: <b>{filteredData.length}</b> records
          </span>
          <div className="flex items-center gap-3">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
